import { supabase } from '@/shared/lib/supabase';

/**
 * adminSettingsService — staff-side reads + super_admin writes for
 * `system_setting` and `service_plan` tables. RLS does the privilege check;
 * non-super_admin updates to system_setting return PostgrestError 42501.
 */

export type SystemSettingRow = {
  key: string;
  value: unknown;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
  /** Resolved from `updated_by` by the view. `null` on a row nobody has edited
   *  since the stamping trigger landed (2026-08-31) — which is every row that
   *  predates it, because `updated_by` was never written before then. Absent
   *  from the single-row shape returned by an update, which reads the table. */
  updated_by_email?: string | null;
};

export type ServicePlanRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  whm_package_name: string;
  recurring_amount: number;
  vat_rate: number;
  billing_cycle: 'monthly' | 'annual' | 'biennial';
  setup_fee: number;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export const adminSettingsService = {
  async listSystemSettings(): Promise<SystemSettingRow[]> {
    // Reads the VIEW, not the table, for `updated_by_email` — resolving a uuid
    // to an email needs auth.users, which the browser cannot read.
    const { data, error } = await supabase
      .from('v_admin_system_setting')
      .select('key, value, description, updated_at, updated_by, updated_by_email')
      .order('key');
    if (error) throw new Error(`Could not load system settings: ${error.message}`);
    return (data ?? []) as SystemSettingRow[];
  },

  async updateSystemSetting(key: string, value: unknown): Promise<SystemSettingRow> {
    // `updated_by` and `updated_at` are stamped by the
    // `system_setting_stamp_actor` trigger from `auth.uid()`. They are
    // deliberately NOT sent from here: a client-supplied actor is a claim, not
    // a fact, and this table holds the kill switches.
    const { data, error } = await supabase
      .from('system_setting')
      .update({ value })
      .eq('key', key)
      .select('key, value, description, updated_at, updated_by')
      .single();
    if (error) {
      if (error.code === '42501') {
        throw new Error('Forbidden — only super_admin can edit system settings.');
      }
      throw new Error(`Update failed: ${error.message}`);
    }
    return data as SystemSettingRow;
  },

  async listPlans(): Promise<ServicePlanRow[]> {
    const { data, error } = await supabase
      .from('service_plan')
      .select(
        'id, name, slug, description, whm_package_name, recurring_amount, vat_rate, billing_cycle, setup_fee, display_order, is_active, created_at, updated_at',
      )
      .order('display_order')
      .order('name');
    if (error) throw new Error(`Could not load plans: ${error.message}`);
    return (data ?? []) as ServicePlanRow[];
  },

  async updatePlan(id: string, patch: Partial<Omit<ServicePlanRow, 'id' | 'created_at' | 'updated_at'>>): Promise<ServicePlanRow> {
    const { data, error } = await supabase
      .from('service_plan')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select(
        'id, name, slug, description, whm_package_name, recurring_amount, vat_rate, billing_cycle, setup_fee, display_order, is_active, created_at, updated_at',
      )
      .single();
    if (error) {
      if (error.code === '42501') {
        throw new Error('Forbidden — staff role required.');
      }
      throw new Error(`Plan update failed: ${error.message}`);
    }
    return data as ServicePlanRow;
  },

  async listStaff(): Promise<Array<{
    id: string;
    email: string | null;
    role: string;
    last_sign_in_at: string | null;
  }>> {
    const { data, error } = await supabase.rpc('list_staff_users');
    if (error) throw new Error(`Could not load staff: ${error.message}`);
    return (data ?? []) as Array<{
      id: string;
      email: string | null;
      role: string;
      last_sign_in_at: string | null;
    }>;
  },

  async listEmailTemplates(): Promise<Array<{
    id: string;
    template_key: string;
    version: number;
    subject: string;
    body_markdown: string;
    is_active: boolean;
    updated_at: string;
  }>> {
    const { data, error } = await supabase
      .from('email_template')
      .select('id, template_key, version, subject, body_markdown, is_active, updated_at')
      .eq('is_active', true)
      .order('template_key');
    if (error) throw new Error(`Could not load email templates: ${error.message}`);
    return (data ?? []) as Array<{
      id: string;
      template_key: string;
      version: number;
      subject: string;
      body_markdown: string;
      is_active: boolean;
      updated_at: string;
    }>;
  },

  /**
   * Updates the active email template by versioning:
   *   1. Mark current active version is_active=false
   *   2. Insert new row version+1 with is_active=true
   * Preserves history (rollback = re-flip is_active).
   */
  async updateEmailTemplate(input: {
    template_key: string;
    current_version: number;
    subject: string;
    body_markdown: string;
  }): Promise<void> {
    const { data: user } = await supabase.auth.getUser();
    const { error: deactErr } = await supabase
      .from('email_template')
      .update({ is_active: false })
      .eq('template_key', input.template_key)
      .eq('version', input.current_version);
    if (deactErr) throw new Error(`Could not deactivate previous version: ${deactErr.message}`);
    const { error: insErr } = await supabase
      .from('email_template')
      .insert({
        template_key: input.template_key,
        version: input.current_version + 1,
        subject: input.subject,
        body_markdown: input.body_markdown,
        is_active: true,
        created_by: user?.user?.id ?? null,
      });
    if (insErr) {
      // Roll back the deactivation so we don't leave the template inactive
      await supabase
        .from('email_template')
        .update({ is_active: true })
        .eq('template_key', input.template_key)
        .eq('version', input.current_version);
      throw new Error(`Could not insert new version: ${insErr.message}`);
    }
  },

  async setStaffRole(input: {
    user_id?: string;
    email?: string;
    role: 'customer' | 'support' | 'staff' | 'admin' | 'super_admin';
  }): Promise<{ user_id: string; email: string; previous_role?: string; new_role: string }> {
    const { data, error } = await supabase.functions.invoke('staff-role-set', {
      method: 'POST',
      body: input,
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Role change failed.');
    }
    return data;
  },
};
